import React, { useState } from "react";
import { View, Text, StyleSheet, TouchableOpacity, TextInput, FlatList, KeyboardAvoidingView, Platform } from "react-native";
import { Ionicons } from "@expo/vector-icons";
import { useTheme } from "../../../context/ThemeContext";
import { ProductRating } from "./ProductRating";

const CommentItem = ({ comment }) => {
	const theme = useTheme();
	const styles = useStyles();

	// Format date
	const formatDate = (dateString) => {
		const date = new Date(dateString);
		return date.toLocaleDateString("en-US", {
			year: "numeric",
			month: "short",
			day: "numeric",
		});
	};

	return (
		<View style={styles.commentItem}>
			<View style={styles.commentHeader}>
				<View style={styles.userInfo}>
					<View style={styles.avatar}>
						<Text style={styles.avatarText}>{comment.user.charAt(0)}</Text>
					</View>
					<Text style={styles.username}>{comment.user}</Text>
				</View>
				<Text style={styles.commentDate}>{formatDate(comment.date)}</Text>
			</View>

			<View style={styles.ratingContainer}>
				<ProductRating rating={comment.rating} size={14} showEmpty={false} />
				<Text style={styles.ratingText}>{comment.rating.toFixed(1)}</Text>
			</View>

			<Text style={styles.commentText}>{comment.text}</Text>

			<View style={styles.commentActions}>
				<TouchableOpacity style={styles.actionButton}>
					<Ionicons name="thumbs-up-outline" size={16} color={theme.colors.textSecondary} />
					<Text style={styles.actionText}>Helpful</Text>
				</TouchableOpacity>
				<TouchableOpacity style={styles.actionButton}>
					<Ionicons name="flag-outline" size={16} color={theme.colors.textSecondary} />
					<Text style={styles.actionText}>Report</Text>
				</TouchableOpacity>
			</View>
		</View>
	);
};

export const CommentSection = ({ comments = [], productId, onAddComment }) => {
	const theme = useTheme();
	const styles = useStyles();
	const [newComment, setNewComment] = useState("");
	const [selectedRating, setSelectedRating] = useState(5);
	const [isAddingComment, setIsAddingComment] = useState(false);

	const handleSubmitComment = () => {
		if (newComment.trim() === "") return;

		onAddComment({
			id: `comment-${Date.now()}`,
			productId,
			user: "You", // In a real app, this would come from the user profile
			text: newComment,
			rating: selectedRating,
			date: new Date().toISOString(),
		});

		// Reset form
		setNewComment("");
		setSelectedRating(5);
		setIsAddingComment(false);
	};

	const handleRatingPress = (rating) => {
		setSelectedRating(rating);
	};

	return (
		<KeyboardAvoidingView behavior={Platform.OS === "ios" ? "padding" : "height"} style={styles.container}>
			<View style={styles.header}>
				<Text style={styles.title}>Customer Reviews</Text>
				<TouchableOpacity style={styles.addButton} onPress={() => setIsAddingComment(!isAddingComment)}>
					<Ionicons name={isAddingComment ? "close-outline" : "add-outline"} size={20} color={theme.colors.primary} />
					<Text style={styles.addButtonText}>{isAddingComment ? "Cancel" : "Write a Review"}</Text>
				</TouchableOpacity>
			</View>

			{isAddingComment && (
				<View style={styles.addCommentContainer}>
					<Text style={styles.ratingLabel}>Your Rating</Text>
					<View style={styles.ratingSelector}>
						{[1, 2, 3, 4, 5].map((rating) => (
							<TouchableOpacity key={`rating-${rating}`} onPress={() => handleRatingPress(rating)} style={styles.ratingStar}>
								<Ionicons name={rating <= selectedRating ? "star" : "star-outline"} size={28} color={theme.colors.warning} />
							</TouchableOpacity>
						))}
					</View>

					<TextInput style={styles.commentInput} placeholder="Share your thoughts about this product..." placeholderTextColor={theme.colors.textSecondary} value={newComment} onChangeText={setNewComment} multiline maxLength={500} />

					<TouchableOpacity style={[styles.submitButton, !newComment.trim() && styles.submitButtonDisabled]} onPress={handleSubmitComment} disabled={!newComment.trim()}>
						<Text style={styles.submitButtonText}>Submit Review</Text>
					</TouchableOpacity>
				</View>
			)}

			{comments.length > 0 ? (
				<FlatList data={comments} renderItem={({ item }) => <CommentItem comment={item} />} keyExtractor={(item) => item.id} contentContainerStyle={styles.commentsList} />
			) : (
				<View style={styles.emptyState}>
					<Ionicons name="chatbubble-outline" size={40} color={theme.colors.textSecondary} />
					<Text style={styles.emptyStateText}>No reviews yet. Be the first to review this product!</Text>
				</View>
			)}
		</KeyboardAvoidingView>
	);
};

const useStyles = () => {
	const theme = useTheme();

	return StyleSheet.create({
		container: {
			flex: 1,
			backgroundColor: theme.colors.systemBackground.primary,
			borderRadius: theme.borderRadius.lg,
			padding: theme.spacing.md,
			marginTop: theme.spacing.md,
		},
		header: {
			flexDirection: "row",
			justifyContent: "space-between",
			alignItems: "center",
			marginBottom: theme.spacing.md,
		},
		title: {
			...theme.typography.title3,
			color: theme.colors.text,
			fontWeight: "700",
		},
		addButton: {
			flexDirection: "row",
			alignItems: "center",
		},
		addButtonText: {
			...theme.typography.subhead,
			color: theme.colors.primary,
			fontWeight: "600",
			marginLeft: 4,
		},
		addCommentContainer: {
			backgroundColor: theme.colors.systemBackground.secondary,
			padding: theme.spacing.md,
			borderRadius: theme.borderRadius.md,
			marginBottom: theme.spacing.md,
		},
		ratingLabel: {
			...theme.typography.subhead,
			color: theme.colors.textSecondary,
			marginBottom: theme.spacing.xs,
		},
		ratingSelector: {
			flexDirection: "row",
			marginBottom: theme.spacing.md,
		},
		ratingStar: {
			marginRight: theme.spacing.xs,
		},
		commentInput: {
			backgroundColor: theme.colors.systemBackground.primary,
			borderRadius: theme.borderRadius.md,
			padding: theme.spacing.sm,
			minHeight: 100,
			...theme.typography.body,
			color: theme.colors.text,
			textAlignVertical: "top",
			marginBottom: theme.spacing.md,
		},
		submitButton: {
			backgroundColor: theme.colors.primary,
			padding: theme.spacing.sm,
			borderRadius: theme.borderRadius.md,
			alignItems: "center",
		},
		submitButtonDisabled: {
			backgroundColor: theme.colors.systemBackground.tertiary,
		},
		submitButtonText: {
			...theme.typography.subhead,
			color: "#fff",
			fontWeight: "600",
		},
		commentsList: {
			paddingBottom: theme.spacing.lg,
		},
		commentItem: {
			borderBottomWidth: 1,
			borderBottomColor: theme.colors.separator,
			paddingVertical: theme.spacing.md,
		},
		commentHeader: {
			flexDirection: "row",
			justifyContent: "space-between",
			alignItems: "center",
			marginBottom: theme.spacing.xs,
		},
		userInfo: {
			flexDirection: "row",
			alignItems: "center",
		},
		avatar: {
			width: 30,
			height: 30,
			borderRadius: 15,
			backgroundColor: theme.colors.systemBackground.tertiary,
			justifyContent: "center",
			alignItems: "center",
			marginRight: theme.spacing.xs,
		},
		avatarText: {
			...theme.typography.footnote,
			fontWeight: "600",
			color: theme.colors.text,
		},
		username: {
			...theme.typography.subhead,
			color: theme.colors.text,
			fontWeight: "600",
		},
		commentDate: {
			...theme.typography.caption,
			color: theme.colors.textSecondary,
		},
		ratingContainer: {
			flexDirection: "row",
			alignItems: "center",
			marginVertical: theme.spacing.xs,
		},
		ratingText: {
			...theme.typography.footnote,
			color: theme.colors.textSecondary,
			fontWeight: "500",
			marginLeft: theme.spacing.xs,
		},
		commentText: {
			...theme.typography.body,
			color: theme.colors.text,
			marginVertical: theme.spacing.xs,
		},
		commentActions: {
			flexDirection: "row",
			marginTop: theme.spacing.xs,
		},
		actionButton: {
			flexDirection: "row",
			alignItems: "center",
			marginRight: theme.spacing.md,
		},
		actionText: {
			...theme.typography.footnote,
			color: theme.colors.textSecondary,
			marginLeft: 4,
		},
		emptyState: {
			alignItems: "center",
			justifyContent: "center",
			padding: theme.spacing.xl,
		},
		emptyStateText: {
			...theme.typography.body,
			color: theme.colors.textSecondary,
			textAlign: "center",
			marginTop: theme.spacing.md,
		},
	});
};
